import { useState, useEffect } from 'react'
import axios from 'axios'
import { Spinner } from 'reactstrap'
import { Star, CheckCircle, AlertCircle, Clock, MessageSquare, ThumbsUp } from 'react-feather'

const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN)

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
})

const BASE = process.env.REACT_APP_BASEURL

const BrandSampleSurveyCard = ({ message, participateRequestId }) => {
  const [loading, setLoading] = useState(true)
  const [surveyData, setSurveyData] = useState(null)

  const reqId = participateRequestId || message?.participate_request_id

  useEffect(() => {
    if (!reqId) {
      setLoading(false)
      return
    }

    axios.get(`${BASE}brand/sample-feedback/detail?participate_request_id=${reqId}`, authHeaders())
      .then(res => {
        if (res.data?.success) {
          setSurveyData(res.data)
        }
      })
      .catch(() => { /* Silent fallback */ })
      .finally(() => setLoading(false))
  }, [reqId])

  if (loading) {
    return (
      <div className="chat-survey-card font-inter">
        <div className="d-flex align-items-center">
          <Spinner size="sm" color="primary" className="mr-75" />
          <span style={{ fontSize: '13px', color: '#64748b' }}>Loading feedback status...</span>
        </div>
      </div>
    )
  }

  if (!surveyData) {
    return (
      <div className="chat-content">
        <div dangerouslySetInnerHTML={{ __html: message?.message || '' }} />
      </div>
    )
  }

  const { status, rd_name, campaign_title, completed_at, answers } = surveyData
  const rating = answers?.q3_rating ? parseInt(answers.q3_rating, 10) : null

  // ── 1. Completed Feedback Received ──────────────────────────────────────────
  if (status === 'completed') {
    return (
      <div className="chat-survey-card font-inter">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between pb-50 mb-75 border-bottom">
          <div className="d-flex align-items-center">
            <CheckCircle size={18} className="text-success mr-50" />
            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14.5px' }}>
              Sample Feedback Received
            </span>
          </div>
          <span
            style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#15803d',
              background: '#dcfce7',
              padding: '2px 8px',
              borderRadius: '12px'
            }}
          >
            Completed
          </span>
        </div>

        {/* Subtitle */}
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px', lineHeight: '1.45' }}>
          <strong style={{ color: '#1e293b' }}>{rd_name || 'Dietitian'}</strong> submitted feedback {completed_at ? `on ${completed_at}` : ''} for <em>{campaign_title}</em>
        </p>

        {/* Rating */}
        {rating && (
          <div className="d-flex align-items-center mb-50">
            <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600', marginRight: '8px' }}>
              Overall Rating:
            </span>
            <div className="d-flex align-items-center" style={{ gap: '3px' }}>
              {[1, 2, 3, 4, 5].map(n => (
                <Star
                  key={n}
                  size={16}
                  fill={n <= rating ? '#f59e0b' : 'none'}
                  stroke={n <= rating ? '#f59e0b' : '#cbd5e1'}
                />
              ))}
              <span style={{ fontSize: '12.5px', fontWeight: '700', marginLeft: '6px', color: '#1e293b' }}>
                ({rating}/5)
              </span>
            </div>
          </div>
        )}

        {/* Recommendation */}
        {answers?.q5_recommend && (
          <div className="d-flex align-items-center mb-50">
            <ThumbsUp size={14} className="text-primary mr-50" />
            <span style={{ fontSize: '13px', color: '#475569', marginRight: '6px' }}>Would recommend to clients:</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{answers.q5_recommend}</span>
          </div>
        )}

        {/* Collaboration */}
        {answers?.q6_work_again && (
          <div className="d-flex align-items-center mb-50">
            <span style={{ fontSize: '13px', color: '#475569', marginRight: '6px' }}>Open to future work:</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{answers.q6_work_again}</span>
          </div>
        )}

        {/* Comments Box */}
        {answers?.q4_comment && answers.q4_comment !== '(skipped)' && (
          <div className="comment-quote-box">
            <div className="comment-label d-flex align-items-center">
              <MessageSquare size={12} className="mr-50" />
              Dietitian Comments
            </div>
            <p className="comment-text">
              &ldquo;{answers.q4_comment}&rdquo;
            </p>
          </div>
        )}
      </div>
    )
  }

  // ── 2. Flagged (Sample not received) ───────────────────────────────────────
  if (status === 'flagged') {
    return (
      <div
        className="chat-survey-card font-inter"
        style={{
          background: '#fffbf0 !important',
          borderColor: '#fde68a !important'
        }}
      >
        <div className="d-flex align-items-start">
          <AlertCircle size={22} className="text-warning mr-75 flex-shrink-0 mt-25" />
          <div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: '#b91c1c',
                background: '#fee2e2',
                padding: '2px 8px',
                borderRadius: '10px',
                display: 'inline-block',
                marginBottom: '6px'
              }}
            >
              Sample Not Received
            </span>
            <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
              {rd_name || 'Dietitian'} reported samples did not arrive
            </h6>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: 0, lineHeight: '1.45' }}>
              The dietitian indicated their free samples have not arrived yet. Please verify your shipment tracking or send a quick message to coordinate.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── 3. Paused State ────────────────────────────────────────────────────────
  if (status === 'paused') {
    const isDeliveryPause = answers?.q1_received === 'Still waiting'
    return (
      <div className="chat-survey-card font-inter">
        <div className="d-flex align-items-start">
          <Clock size={20} className="text-primary mr-75 flex-shrink-0 mt-25" />
          <div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: '#5b57ca',
                background: '#ede9fe',
                padding: '2px 8px',
                borderRadius: '10px',
                display: 'inline-block',
                marginBottom: '6px'
              }}
            >
              {isDeliveryPause ? 'Delivery In Progress' : 'Feedback Paused'}
            </span>
            <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
              {isDeliveryPause ? `${rd_name || 'Dietitian'} is still waiting for samples` : 'Dietitian hasn’t tried samples yet'}
            </h6>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: 0, lineHeight: '1.45' }}>
              {isDeliveryPause
                ? 'The dietitian indicated their product samples have not arrived yet. The system will check back with them automatically in 7 days.'
                : `${rd_name || 'The dietitian'} received the samples and will be prompted again in 7 days to share their review.`
              }
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── 4. Pending Survey ──────────────────────────────────────────────────────
  return (
    <div className="chat-survey-card font-inter">
      <div className="d-flex align-items-start">
        <Clock size={20} className="text-primary mr-75 flex-shrink-0 mt-25" />
        <div>
          <span
            style={{
              fontSize: '11px',
              fontWeight: '700',
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: '#5b57ca',
              background: '#ede9fe',
              padding: '2px 8px',
              borderRadius: '10px',
              display: 'inline-block',
              marginBottom: '6px'
            }}
          >
            Feedback Requested
          </span>
          <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
            Sample Feedback In Progress
          </h6>
          <p style={{ fontSize: '13px', color: '#475569', marginBottom: 0, lineHeight: '1.45' }}>
            Feedback questionnaire was sent to <strong style={{ color: '#1e293b' }}>{rd_name || 'the dietitian'}</strong> 14 days after sample dispatch. Waiting for dietitian response.
          </p>
        </div>
      </div>
    </div>
  )
}

export default BrandSampleSurveyCard
